import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  TApiContragent,
  TApiSupervisorContragentBlock,
  TApiSupervisorContragentBlocksResponse,
} from '~/modules/covenants/types.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import api from '~/libs/api';
import {
  TCreateSupervisorContragentBlockRequest,
  TDeleteSupervisorContragentBlockRequest,
  TFetchCallCenterOperatorContragentsRequest,
  TFetchSupervisorContragentBlocksRequest,
  TThunkError,
} from '~/store/covenants/types.ts';

export const fetchMyContragents = createAsyncThunk<
  TApiContragent[],
  { code: string },
  { rejectValue: TThunkError }
>('covenantsPage/fetchMyContragents', async ({ code }, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/covenants-api/covenants/me/contragents', {
      params: { code },
    });
    return data.contragents;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchCallCenterOperatorContragents = createAsyncThunk<
  TApiContragent[],
  TFetchCallCenterOperatorContragentsRequest,
  { rejectValue: TThunkError }
>('covenantsPage/fetchCallCenterOperatorContragents', async ({ code }, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/covenants-api/covenants/operator-controller/contragents', {
      params: { code },
    });
    return data.contragents;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchContragentById = createAsyncThunk<
  TApiContragent,
  { id: number },
  { rejectValue: TThunkError }
>('covenantsPage/fetchContragentById', async ({ id }, { rejectWithValue }) => {
  try {
    const { data } = await api.get(`/covenants-api/covenants/contragents/${id}`);
    return data;
  } catch (error) {
    return thunkErrorHandler(error, rejectWithValue);
  }
});

export const fetchSupervisorContragentBlocks = createAsyncThunk<
  TApiSupervisorContragentBlocksResponse,
  TFetchSupervisorContragentBlocksRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/fetchSupervisorContragentBlocks',
  async ({ contragentId, page, size, sort }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/covenants-api/covenants/supervisors/contragents/${contragentId}/blocks`,
        {
          params: {
            page,
            size,
            sort,
          },
        }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const createSupervisorContragentBlock = createAsyncThunk<
  TApiSupervisorContragentBlock,
  TCreateSupervisorContragentBlockRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/createSupervisorContragentBlock',
  async ({ contragentId, email }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/covenants-api/covenants/supervisors/contragents/${contragentId}/blocks`,
        { email }
      );
      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteSupervisorContragentBlock = createAsyncThunk<
  void,
  TDeleteSupervisorContragentBlockRequest,
  { rejectValue: TThunkError }
>(
  'covenantsPage/deleteSupervisorContragentBlock',
  async ({ contragentId, email }, { rejectWithValue }) => {
    try {
      await api.delete(`/covenants-api/covenants/supervisors/contragents/${contragentId}/blocks`, {
        data: { email },
      });
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
